import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Receipt, Printer, Download, CheckCircle2, X, Plus, History,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAYMENT_MODES = [
  { v: 'cash', label: 'Cash' },
  { v: 'upi', label: 'UPI' },
  { v: 'qr', label: 'QR / Static UPI' },
  { v: 'card', label: 'Card' },
  { v: 'other', label: 'Other' },
];

const EMPTY_FORM = {
  tier_id: '',
  quantity: 1,
  buyer_name: '',
  buyer_email: '',
  buyer_phone: '',
  payment_mode: 'cash',
  date_ids: [],
  notes: '',
  send_email: true,
};

/**
 * At-the-door offline ticketing dialog. Lets admins + event organizers mint
 * a ticket on the spot — no Razorpay, no platform/convenience fees.
 *
 * Shows:
 *  - A form to capture buyer + payment-mode (quantity, tier, dates…)
 *  - A history tab listing every offline ticket already issued for the event
 */
export default function OfflineTicketDialog({ open, onOpenChange, event, token, onIssued }) {
  const [tab, setTab] = useState('new');
  const [form, setForm] = useState(EMPTY_FORM);
  const [issuing, setIssuing] = useState(false);
  const [issued, setIssued] = useState(null); // last response — tickets list
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const loadHistory = useCallback(async () => {
    if (!event?.id || !token) return;
    setHistoryLoading(true);
    try {
      const res = await axios.get(
        `${API}/events/${event.id}/offline-tickets`,
        { headers: authHeaders },
      );
      setHistory(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load offline tickets');
    } finally {
      setHistoryLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.id, token]);

  useEffect(() => {
    if (open) {
      setTab('new');
      setForm({ ...EMPTY_FORM, tier_id: event?.ticket_tiers?.[0]?.id || '' });
      setIssued(null);
      setHistory(null);
    }
  }, [open, event?.id]);

  useEffect(() => {
    if (open && tab === 'history') loadHistory();
  }, [open, tab, loadHistory]);

  if (!event) return null;

  const tiers = event.ticket_tiers || [];
  const selectedTier = tiers.find((t) => t.id === form.tier_id) || null;
  const isMultiDay = !!event.is_multi_day;
  const eventDates = event.event_dates || [];

  // Dates allowed for the selected tier
  const allowedDateIds = selectedTier?.valid_date_ids && selectedTier.valid_date_ids.length > 0
    ? selectedTier.valid_date_ids
    : eventDates.map((d) => d.id);

  const unitPrice = selectedTier ? Number(selectedTier.price || 0) : 0;
  const estTotal = unitPrice * Number(form.quantity || 0);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const toggleDate = (did) => {
    const cur = new Set(form.date_ids);
    if (cur.has(did)) cur.delete(did); else cur.add(did);
    update({ date_ids: Array.from(cur) });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.tier_id) return toast.error('Select a ticket type');
    if (!form.buyer_name.trim()) return toast.error('Buyer name is required');
    if (!form.buyer_phone.trim()) return toast.error('Buyer phone is required');
    const payload = {
      tier_id: form.tier_id,
      quantity: Math.max(1, Math.min(50, Number(form.quantity) || 1)),
      buyer_name: form.buyer_name.trim(),
      buyer_phone: form.buyer_phone.trim(),
      buyer_email: form.buyer_email.trim() || null,
      payment_mode: form.payment_mode,
      date_ids: isMultiDay ? form.date_ids : [],
      notes: form.notes.trim() || null,
      send_email: form.send_email && !!form.buyer_email.trim(),
    };
    setIssuing(true);
    try {
      const res = await axios.post(
        `${API}/events/${event.id}/offline-tickets`,
        payload,
        { headers: authHeaders },
      );
      setIssued(res.data);
      toast.success(`${res.data.tickets.length} ticket${res.data.tickets.length > 1 ? 's' : ''} issued`);
      if (onIssued) onIssued(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to issue ticket');
    } finally {
      setIssuing(false);
    }
  };

  const downloadPdf = async (ticketId) => {
    try {
      const res = await axios.get(`${API}/tickets/${ticketId}/pdf`, {
        headers: authHeaders,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `unight-ticket-${ticketId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('PDF download failed');
    }
  };

  const printPdf = async (ticketId) => {
    try {
      const res = await axios.get(`${API}/tickets/${ticketId}/pdf`, {
        headers: authHeaders,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const win = window.open(url, '_blank');
      if (win) {
        win.addEventListener('load', () => { try { win.print(); } catch { /* noop */ } });
      }
    } catch {
      toast.error('Print failed');
    }
  };

  const printAll = async () => {
    if (!issued?.tickets) return;
    for (const t of issued.tickets) {
      // open each PDF in a new tab; browser decides whether to invoke print
      // dialog. Users can also use "Download" individually if print pop-ups
      // are blocked.
      await printPdf(t.id);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0A0A0A] border-zinc-800 text-white w-[calc(100vw-1.5rem)] sm:max-w-3xl max-h-[92vh] overflow-y-auto p-4 sm:p-6"
        data-testid="offline-ticket-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-['Josefin_Sans'] text-xl flex items-center gap-2">
            <Receipt size={20} className="text-[#B5FF00]" /> OFFLINE TICKETING
          </DialogTitle>
          <p className="text-zinc-400 text-xs mt-1 font-mono">
            Issue tickets at the venue · <span className="text-white">{event.title}</span>
          </p>
          <p className="text-[#B5FF00]/80 text-[10px] font-mono tracking-widest mt-1">
            NO PLATFORM / CONVENIENCE FEE · FACE VALUE ONLY
          </p>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-zinc-800 mt-2">
          <button
            type="button"
            onClick={() => setTab('new')}
            className={`py-2 px-3 font-mono text-xs tracking-widest ${
              tab === 'new'
                ? 'text-[#B5FF00] border-b-2 border-[#B5FF00]'
                : 'text-zinc-500 hover:text-white'
            }`}
            data-testid="offline-tab-new"
          >
            <Plus size={12} className="inline mr-1" /> NEW TICKET
          </button>
          <button
            type="button"
            onClick={() => setTab('history')}
            className={`py-2 px-3 font-mono text-xs tracking-widest ${
              tab === 'history'
                ? 'text-[#B5FF00] border-b-2 border-[#B5FF00]'
                : 'text-zinc-500 hover:text-white'
            }`}
            data-testid="offline-tab-history"
          >
            <History size={12} className="inline mr-1" /> HISTORY
          </button>
        </div>

        {tab === 'new' && !issued && (
          <form onSubmit={submit} className="mt-4 space-y-4" data-testid="offline-form">
            {/* Tier + quantity */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <label className="block text-[10px] font-mono tracking-widest text-zinc-500 mb-1">TICKET TYPE</label>
                <select
                  value={form.tier_id}
                  onChange={(e) => update({ tier_id: e.target.value, date_ids: [] })}
                  className="w-full h-10 bg-[#050505] border border-zinc-800 text-white px-3 text-sm focus:border-[#B5FF00] focus:outline-none"
                  data-testid="offline-tier-select"
                >
                  <option value="">— Select tier —</option>
                  {tiers.map((t) => (
                    <option key={t.id} value={t.id} disabled={Number(t.quantity_available) <= 0}>
                      {t.name} · ₹{Number(t.price).toLocaleString('en-IN')} · {t.quantity_available} left
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-mono tracking-widest text-zinc-500 mb-1">QUANTITY</label>
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={form.quantity}
                  onChange={(e) => update({ quantity: e.target.value })}
                  className="w-full h-10 bg-[#050505] border border-zinc-800 text-white px-3 text-sm focus:border-[#B5FF00] focus:outline-none"
                  data-testid="offline-quantity"
                />
              </div>
            </div>

            {/* Multi-day date selector */}
            {isMultiDay && allowedDateIds.length > 0 && (
              <div>
                <label className="block text-[10px] font-mono tracking-widest text-zinc-500 mb-1">
                  SELECT DATE(S){allowedDateIds.length > 1 ? ' — ONE OR MORE' : ''}
                </label>
                <div className="flex flex-wrap gap-2" data-testid="offline-date-picker">
                  {eventDates.filter((d) => allowedDateIds.includes(d.id)).map((d) => {
                    const active = form.date_ids.includes(d.id);
                    return (
                      <button
                        type="button"
                        key={d.id}
                        onClick={() => toggleDate(d.id)}
                        className={`px-3 py-1.5 border font-mono text-xs tracking-wider ${
                          active
                            ? 'border-[#B5FF00] bg-[#B5FF00]/10 text-[#B5FF00]'
                            : 'border-zinc-800 text-zinc-400 hover:border-zinc-600'
                        }`}
                        data-testid={`offline-date-${d.id}`}
                      >
                        {(d.label || d.date || '').toUpperCase()}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[10px] font-mono text-zinc-600 mt-1">
                  Leave empty to issue the full pass (one ticket per valid date).
                </p>
              </div>
            )}

            {/* Buyer info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-mono tracking-widest text-zinc-500 mb-1">BUYER NAME *</label>
                <input
                  type="text"
                  required
                  value={form.buyer_name}
                  onChange={(e) => update({ buyer_name: e.target.value })}
                  className="w-full h-10 bg-[#050505] border border-zinc-800 text-white px-3 text-sm focus:border-[#B5FF00] focus:outline-none"
                  data-testid="offline-buyer-name"
                />
              </div>
              <div>
                <label className="block text-[10px] font-mono tracking-widest text-zinc-500 mb-1">PHONE *</label>
                <input
                  type="tel"
                  required
                  value={form.buyer_phone}
                  onChange={(e) => update({ buyer_phone: e.target.value })}
                  placeholder="+91…"
                  className="w-full h-10 bg-[#050505] border border-zinc-800 text-white px-3 text-sm focus:border-[#B5FF00] focus:outline-none"
                  data-testid="offline-buyer-phone"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-[10px] font-mono tracking-widest text-zinc-500 mb-1">EMAIL (optional, for e-ticket)</label>
                <input
                  type="email"
                  value={form.buyer_email}
                  onChange={(e) => update({ buyer_email: e.target.value })}
                  className="w-full h-10 bg-[#050505] border border-zinc-800 text-white px-3 text-sm focus:border-[#B5FF00] focus:outline-none"
                  data-testid="offline-buyer-email"
                />
              </div>
            </div>

            {/* Payment mode */}
            <div>
              <label className="block text-[10px] font-mono tracking-widest text-zinc-500 mb-1">PAYMENT MODE</label>
              <div className="flex flex-wrap gap-2" data-testid="offline-mode-picker">
                {PAYMENT_MODES.map((m) => (
                  <button
                    type="button"
                    key={m.v}
                    onClick={() => update({ payment_mode: m.v })}
                    className={`px-3 py-1.5 border font-mono text-xs tracking-wider ${
                      form.payment_mode === m.v
                        ? 'border-[#B5FF00] bg-[#B5FF00]/10 text-[#B5FF00]'
                        : 'border-zinc-800 text-zinc-400 hover:border-zinc-600'
                    }`}
                    data-testid={`offline-mode-${m.v}`}
                  >
                    {m.label.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-[10px] font-mono tracking-widest text-zinc-500 mb-1">NOTES (OPTIONAL)</label>
              <textarea
                rows={2}
                value={form.notes}
                onChange={(e) => update({ notes: e.target.value })}
                placeholder="Collected by, change given, anything you want to remember…"
                className="w-full bg-[#050505] border border-zinc-800 text-white px-3 py-2 text-sm focus:border-[#B5FF00] focus:outline-none"
                data-testid="offline-notes"
              />
            </div>

            {/* Email toggle */}
            {form.buyer_email && (
              <label className="flex items-center gap-2 text-xs text-zinc-400 font-mono" data-testid="offline-send-email">
                <input
                  type="checkbox"
                  checked={form.send_email}
                  onChange={(e) => update({ send_email: e.target.checked })}
                />
                Email the e-ticket with QR to the buyer
              </label>
            )}

            {/* Total */}
            {selectedTier && (
              <div className="border border-zinc-800 bg-[#050505] p-3 flex items-center justify-between" data-testid="offline-total">
                <div className="font-mono text-[10px] tracking-widest text-zinc-500">
                  AMOUNT COLLECTED · {form.quantity} × ₹{unitPrice.toLocaleString('en-IN')}
                </div>
                <div className="text-2xl font-['Josefin_Sans'] text-[#B5FF00]">
                  ₹{estTotal.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                </div>
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <button
                type="submit"
                disabled={issuing || !selectedTier}
                className="flex-1 h-11 bg-[#B5FF00] text-black font-mono text-sm tracking-widest hover:bg-[#B5FF00]/90 disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="offline-issue-submit"
              >
                {issuing ? <Loader2 size={14} className="animate-spin" /> : <Receipt size={14} />}
                ISSUE TICKET
              </button>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="h-11 px-4 border border-zinc-800 text-zinc-400 font-mono text-xs tracking-widest hover:text-white"
                data-testid="offline-cancel"
              >
                CANCEL
              </button>
            </div>
          </form>
        )}

        {tab === 'new' && issued && (
          <div className="mt-4 space-y-4" data-testid="offline-issued">
            <div className="border border-[#B5FF00]/40 bg-[#B5FF00]/5 p-4 flex items-start gap-3">
              <CheckCircle2 size={24} className="text-[#B5FF00] shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-['Josefin_Sans'] text-lg text-white">TICKET{issued.tickets.length > 1 ? 'S' : ''} ISSUED</div>
                <div className="font-mono text-[11px] text-zinc-400 mt-0.5">
                  {issued.tickets.length} ticket{issued.tickets.length > 1 ? 's' : ''} · ₹{Number(issued.total_amount).toLocaleString('en-IN')} collected ({issued.payment_mode.toUpperCase()})
                </div>
              </div>
              <button
                type="button"
                onClick={() => { setIssued(null); setForm({ ...EMPTY_FORM, tier_id: form.tier_id }); }}
                className="text-zinc-400 hover:text-white"
                data-testid="offline-reset"
                title="Issue another"
              >
                <X size={16} />
              </button>
            </div>

            {issued.tickets.length > 1 && (
              <button
                type="button"
                onClick={printAll}
                className="w-full h-10 border border-zinc-800 text-white font-mono text-xs tracking-widest hover:border-[#B5FF00] hover:text-[#B5FF00] flex items-center justify-center gap-2"
                data-testid="offline-print-all"
              >
                <Printer size={14} /> PRINT ALL ({issued.tickets.length})
              </button>
            )}

            <div className="space-y-2">
              {issued.tickets.map((t) => (
                <div key={t.id} className="border border-zinc-800 bg-[#050505] p-3 flex items-center justify-between gap-3" data-testid={`offline-issued-${t.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-['Josefin_Sans'] text-white truncate">{t.attendee_name}</div>
                    <div className="font-mono text-[10px] text-zinc-500 mt-0.5">
                      {t.tier_name.toUpperCase()}
                      {t.seat_number && ` · SEAT ${t.seat_number}`}
                      {t.event_date_label && ` · ${t.event_date_label.toUpperCase()}`}
                      {!t.event_date_label && t.event_date && ` · ${t.event_date}`}
                    </div>
                    <div className="font-mono text-[10px] text-zinc-600 mt-0.5">{t.qr_code}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => downloadPdf(t.id)}
                      className="h-9 w-9 border border-zinc-800 text-zinc-400 hover:text-[#B5FF00] hover:border-[#B5FF00] flex items-center justify-center"
                      title="Download PDF"
                      data-testid={`offline-download-${t.id}`}
                    >
                      <Download size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => printPdf(t.id)}
                      className="h-9 w-9 border border-zinc-800 text-zinc-400 hover:text-[#B5FF00] hover:border-[#B5FF00] flex items-center justify-center"
                      title="Print"
                      data-testid={`offline-print-${t.id}`}
                    >
                      <Printer size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === 'history' && (
          <div className="mt-4" data-testid="offline-history">
            {historyLoading ? (
              <div className="flex items-center justify-center py-10 text-zinc-500 text-xs font-mono">
                <Loader2 size={14} className="animate-spin mr-2" /> LOADING…
              </div>
            ) : !history || history.tickets.length === 0 ? (
              <p className="text-zinc-500 text-sm text-center py-10">No offline tickets issued yet for this event.</p>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="border border-zinc-800 bg-[#050505] p-3">
                    <div className="font-mono text-[10px] text-zinc-500 tracking-widest">OFFLINE TICKETS</div>
                    <div className="text-2xl text-white font-['Josefin_Sans'] mt-1">{history.totals.count}</div>
                  </div>
                  <div className="border border-zinc-800 bg-[#050505] p-3">
                    <div className="font-mono text-[10px] text-zinc-500 tracking-widest">COLLECTED</div>
                    <div className="text-2xl text-[#B5FF00] font-['Josefin_Sans'] mt-1">
                      ₹{Number(history.totals.revenue).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                    </div>
                  </div>
                </div>
                <div className="border border-zinc-800 overflow-x-auto">
                  <table className="w-full text-sm min-w-[640px]">
                    <thead>
                      <tr className="border-b border-zinc-800 bg-[#050505]">
                        <th className="text-left py-2 px-3 font-mono text-[10px] text-zinc-500 tracking-widest">BUYER</th>
                        <th className="text-left py-2 px-3 font-mono text-[10px] text-zinc-500 tracking-widest">TIER</th>
                        <th className="text-left py-2 px-3 font-mono text-[10px] text-zinc-500 tracking-widest">MODE</th>
                        <th className="text-right py-2 px-3 font-mono text-[10px] text-zinc-500 tracking-widest">PRICE</th>
                        <th className="text-left py-2 px-3 font-mono text-[10px] text-zinc-500 tracking-widest">ISSUED BY</th>
                        <th className="text-left py-2 px-3 font-mono text-[10px] text-zinc-500 tracking-widest">STATUS</th>
                        <th className="text-right py-2 px-3 font-mono text-[10px] text-zinc-500 tracking-widest">PDF</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.tickets.map((t) => (
                        <tr key={t.id} className="border-b border-zinc-900" data-testid={`offline-history-row-${t.id}`}>
                          <td className="py-2 px-3 text-white">
                            <div className="font-['Josefin_Sans']">{t.attendee_name}</div>
                            <div className="font-mono text-[10px] text-zinc-500">{t.attendee_phone}</div>
                          </td>
                          <td className="py-2 px-3 text-zinc-300 font-mono text-xs">{t.tier_name}{t.seat_number ? ` · ${t.seat_number}` : ''}</td>
                          <td className="py-2 px-3 text-zinc-300 font-mono text-xs">{(t.payment_mode || 'cash').toUpperCase()}</td>
                          <td className="py-2 px-3 text-right text-white font-mono">
                            ₹{Number(t.total_price).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                          </td>
                          <td className="py-2 px-3 text-zinc-400 font-mono text-xs">{t.issued_by}</td>
                          <td className="py-2 px-3 font-mono text-[10px] tracking-widest">
                            {t.checked_in ? (
                              <span className="text-[#B5FF00]">CHECKED IN</span>
                            ) : (
                              <span className="text-zinc-500">PENDING</span>
                            )}
                          </td>
                          <td className="py-2 px-3 text-right">
                            <button
                              type="button"
                              onClick={() => downloadPdf(t.id)}
                              className="text-zinc-400 hover:text-[#B5FF00]"
                              title="Download PDF"
                              data-testid={`offline-history-download-${t.id}`}
                            >
                              <Download size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
